import { useEffect, useState } from 'react';
import { ArrowDown, ArrowUp } from 'lucide-react';
import {
  HOME_SECTION_DESCRIPTIONS, HOME_SECTION_LABELS, HOME_TEMPLATES, type HomeLayout, type HomeSectionId,
} from '@unitly/contracts';
import { Sheet } from '../../ui/Sheet.tsx';
import { useHomeLayout, useSetHomeLayout } from '../../api/queries.ts';
import { useOrgCtx } from '../../app/org-context.tsx';
import { useToast } from '../../ui/Toast.tsx';
import { PageLoader } from '../../ui/primitives.tsx';

function templateMatches(layout: HomeLayout, sections: HomeSectionId[]) {
  const visible = layout.sections.filter((s) => s.visible).map((s) => s.id);
  return visible.length === sections.length && visible.every((id, i) => id === sections[i]);
}

export function HomeCustomizeSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { orgId } = useOrgCtx();
  const saved = useHomeLayout(orgId);
  const setLayout = useSetHomeLayout(orgId);
  const toast = useToast();
  const [draft, setDraft] = useState<HomeLayout | null>(null);

  useEffect(() => {
    if (open && saved.data) setDraft(saved.data);
  }, [open, saved.data]);

  const applyTemplate = (sections: HomeSectionId[]) => {
    if (!draft) return;
    const order = [...sections, ...draft.sections.map((s) => s.id).filter((id) => !sections.includes(id))];
    setDraft({ sections: order.map((id) => ({ ...draft.sections.find((s) => s.id === id)!, visible: sections.includes(id) })) });
  };
  const toggle = (id: HomeSectionId) => draft && setDraft({ sections: draft.sections.map((s) => (s.id === id ? { ...s, visible: !s.visible } : s)) });
  const move = (i: number, dir: -1 | 1) => {
    if (!draft) return;
    const sections = [...draft.sections];
    const j = i + dir;
    if (j < 0 || j >= sections.length) return;
    [sections[i], sections[j]] = [sections[j]!, sections[i]!];
    setDraft({ sections });
  };

  const save = () => {
    if (!draft) return;
    setLayout.mutate(draft, { onSuccess: () => { toast('Home aggiornata'); onClose(); }, onError: () => toast('Non è stato possibile salvare') });
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Personalizza Home"
      variant="center"
      labelledBy="home-customize-title"
      footer={
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={onClose}>Annulla</button>
          <button type="button" className="btn btn-primary" style={{ flex: 1 }} disabled={!draft || setLayout.isPending} onClick={save}>Salva</button>
        </div>
      }
    >
      {(!draft || saved.isLoading) && <PageLoader />}
      {draft && (
        <>
          <p className="hint" style={{ marginTop: 0 }}>Scegli un modello di partenza, poi mostra, nascondi e riordina le sezioni come vuoi.</p>
          <div className="template-row" role="group" aria-label="Modelli">
            {HOME_TEMPLATES.map((t) => (
              <button key={t.id} type="button" className="template-card" aria-pressed={templateMatches(draft, t.sections)} onClick={() => applyTemplate(t.sections)}>
                <strong>{t.name}</strong>
                <span>{t.description}</span>
              </button>
            ))}
          </div>

          <h3 className="subhead">Sezioni</h3>
          <div className="card list">
            {draft.sections.map((s, i) => (
              <div key={s.id} className="section-row">
                <span className="grow">
                  {HOME_SECTION_LABELS[s.id]}
                  <small>{HOME_SECTION_DESCRIPTIONS[s.id]}</small>
                </span>
                <span className="order-btns">
                  <button type="button" className="icon-btn muted" aria-label="Sposta su" disabled={i === 0} onClick={() => move(i, -1)}><ArrowUp size={16} /></button>
                  <button type="button" className="icon-btn muted" aria-label="Sposta giù" disabled={i === draft.sections.length - 1} onClick={() => move(i, 1)}><ArrowDown size={16} /></button>
                </span>
                <button type="button" role="switch" className="switch" aria-checked={s.visible} aria-label={`Mostra ${HOME_SECTION_LABELS[s.id]}`} onClick={() => toggle(s.id)} />
              </div>
            ))}
          </div>
        </>
      )}
    </Sheet>
  );
}
